import { useState } from 'react'
import { Bell, Sun, Moon, Search, X, CheckCircle2, AlertTriangle, Info, Rocket } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useThemeStore } from '@/stores/themeStore'
import { UserProfile } from '@/components/UserProfile'
import styles from './TopBar.module.css'

const PAGE_TITLES: Record<string, string> = {
  '/dashboard':          'Platform Central',
  '/deployments':        'All Deployments',
  '/deployments/new':    'New Environment Deployment',
  '/app-in-aks':         'New Application in AKS',
  '/mcp-deployment':     'MCP Server Deployment',
  '/agent-deployment':   'Agent Deployment',
  '/release-management': 'Release Management',
  '/patch-management':   'Patch Management',
  '/haproxy':            'HAProxy Configuration',
  '/cloudflare':         'Cloudflare Configuration',
  '/certificates':       'Certificate Management',
  '/status-central':     'Status Central',
  '/monitoring':         'Live Monitoring',
  '/cost-optimization':  'Cost Optimization',
  '/cost-reports':       'Cost Reports',
  '/service-desk':       'Service Desk',
  '/products':           'Product Catalog',
  '/admin':              'Admin Console',
}

interface Notification {
  id: string
  type: 'success' | 'warning' | 'info'
  title: string
  body: string
  time: string
  read: boolean
}

const MOCK_NOTIFICATIONS: Notification[] = [
  { id: '1', type: 'success', title: 'Deployment Completed',   body: 'AppCentral prod deployment finished successfully.', time: '2m ago',  read: false },
  { id: '2', type: 'warning', title: 'Certificate Expiring',   body: 'SSL cert for tenant-acme.aptean.app expires in 14 days.', time: '1h ago',  read: false },
  { id: '3', type: 'info',    title: 'Patch Available',        body: 'AKS node pool update v1.29.4 is available.', time: '3h ago',  read: false },
  { id: '4', type: 'success', title: 'HAProxy Reloaded',       body: 'HAProxy config applied to demo environment.', time: '5h ago',  read: true  },
  { id: '5', type: 'info',    title: 'Cost Report Ready',      body: 'April 2026 cost report is available for review.', time: '1d ago',  read: true  },
]

const NOTIF_ICON = { success: CheckCircle2, warning: AlertTriangle, info: Info }
const NOTIF_COLOR = { success: 'var(--aptean-success)', warning: 'var(--aptean-warning)', info: 'var(--aptean-info)' }

export function TopBar() {
  const navigate   = useNavigate()
  const location   = useLocation()
  const { theme, toggle } = useThemeStore()

  const [notifOpen, setNotifOpen]   = useState(false)
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS)
  const [search, setSearch]         = useState('')

  const title = Object.entries(PAGE_TITLES).find(([path]) =>
    path === location.pathname || location.pathname.startsWith(path + '/')
  )?.[1] ?? 'Platform Central'

  const unread = notifications.filter(n => !n.read).length

  const markAllRead = () =>
    setNotifications(ns => ns.map(n => ({ ...n, read: true })))

  return (
    <>
      <header className={styles.topbar}>
        {/* Page title */}
        <div className={styles.left}>
          <h1 className={styles.pageTitle}>{title}</h1>
        </div>

        {/* Global search */}
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search modules, deployments..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className={styles.searchClear} onClick={() => setSearch('')}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Right actions */}
        <div className={styles.right}>
          <button
            className={styles.iconBtn}
            onClick={toggle}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          <button
            className={styles.iconBtn}
            onClick={() => setNotifOpen(o => !o)}
            title="Notifications"
          >
            <Bell size={16} />
            {unread > 0 && <span className={styles.badge}>{unread}</span>}
          </button>

          <button
            className={styles.iconBtn}
            title="New Deployment"
            onClick={() => navigate('/deployments/new')}
          >
            <Rocket size={16} />
          </button>

          <div className={styles.divider} />

          <UserProfile />
        </div>
      </header>

      {/* Notification panel */}
      {notifOpen && (
        <>
          <div className={styles.notifBackdrop} onClick={() => setNotifOpen(false)} />
          <div className={styles.notifPanel}>
            <div className={styles.notifHeader}>
              <div>
                <div className={styles.notifTitle}>Notifications</div>
                {unread > 0 && <div className={styles.notifSub}>{unread} unread</div>}
              </div>
              <button className={styles.markRead} onClick={markAllRead}>Mark all read</button>
            </div>

            <div className={styles.notifList}>
              {notifications.map(n => {
                const Icon = NOTIF_ICON[n.type]
                return (
                  <div
                    key={n.id}
                    className={`${styles.notifItem} ${n.read ? styles.notifRead : ''}`}
                    onClick={() => setNotifications(ns =>
                      ns.map(x => x.id === n.id ? { ...x, read: true } : x)
                    )}
                  >
                    <Icon size={16} style={{ color: NOTIF_COLOR[n.type], flexShrink: 0, marginTop: 2 }} />
                    <div className={styles.notifBody}>
                      <div className={styles.notifItemTitle}>{n.title}</div>
                      <div className={styles.notifItemBody}>{n.body}</div>
                      <div className={styles.notifTime}>{n.time}</div>
                    </div>
                    {!n.read && <span className={styles.notifDot} />}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </>
  )
}
